<template>
  <div class="notification-templates-tab">
    <div class="nt-header">
      <h3>{{ $t('admin_page.notification_templates') }}</h3>
      <p>{{ $t('admin_page.notification_templates_description') }}</p>
    </div>

    <notification-event-settings-table
      :event-settings="eventSettings"
      :templates="templates"
      :is-loading="isLoadingEventSettings"
      :saving-event-key="savingEventKey"
      @save-assignment="saveEventAssignment"
    />

    <notification-templates-table
      :templates="templates"
      :is-loading="isLoading"
      @edit="openEditTemplate"
      @archive="archiveTemplate"
    />

    <notification-template-editor-modal
      v-if="isEditorOpen"
      :template="selectedTemplate"
      :is-saving="isSaving"
      @close="closeEditor"
      @save="saveTemplate"
    />
  </div>
</template>

<script>
import NotificationTemplateEditorModal from './NotificationTemplateEditorModal';
import NotificationEventSettingsTable from './NotificationEventSettingsTable';
import NotificationTemplatesTable from './NotificationTemplatesTable';
import {
  ARCHIVE_NOTIFICATION_TEMPLATE,
  CREATE_NOTIFICATION_TEMPLATE,
  GET_NOTIFICATION_EVENT_SETTINGS,
  GET_NOTIFICATION_TEMPLATES,
  UPDATE_NOTIFICATION_EVENT_SETTING,
  UPDATE_NOTIFICATION_TEMPLATE,
} from '../../../store/notifications/actions/actionTypes';

export default {
  name: 'notification-templates-tab',
  components: {
    NotificationEventSettingsTable,
    NotificationTemplateEditorModal,
    NotificationTemplatesTable,
  },
  props: {
    search: String,
  },
  data() {
    return {
      templates: [],
      eventSettings: [],
      isLoading: false,
      isLoadingEventSettings: false,
      isSaving: false,
      savingEventKey: '',
      saveTimersByEvent: {},
      pendingEventAssignments: {},
      isEditorOpen: false,
      selectedTemplate: null,
    };
  },
  async mounted() {
    await Promise.all([
      this.getNotificationTemplates({ search: this.search }),
      this.getNotificationEventSettings(),
    ]);
  },
  methods: {
    async getNotificationTemplates(query = {}) {
      this.isLoading = true;
      try {
        this.templates = await this.$store.dispatch(
          GET_NOTIFICATION_TEMPLATES,
          {
            search: query.search || '',
          }
        );
      } catch (error) {
        console.error('Error fetching notification templates:', error);
        this.$buefy.snackbar.open({
          message: this.$t('error'),
          type: 'is-danger',
        });
      } finally {
        this.isLoading = false;
      }
    },
    async getNotificationEventSettings() {
      this.isLoadingEventSettings = true;
      try {
        this.eventSettings = await this.$store.dispatch(
          GET_NOTIFICATION_EVENT_SETTINGS
        );
      } catch (error) {
        console.error('Error fetching notification event settings:', error);
        this.$buefy.snackbar.open({
          message: this.$t('error'),
          type: 'is-danger',
        });
      } finally {
        this.isLoadingEventSettings = false;
      }
    },
    applyLocalEventAssignment(eventKey, templateId, enabled) {
      const assignedTemplate = templateId
        ? this.templates.find((template) => template._id === templateId) || null
        : null;

      this.eventSettings = (this.eventSettings || []).map((eventSetting) => {
        if (eventSetting.eventKey !== eventKey) {
          return eventSetting;
        }

        return {
          ...eventSetting,
          assignedTemplateId: templateId || null,
          assignedTemplate,
          enabled: enabled === undefined ? eventSetting.enabled : enabled,
        };
      });
    },
    mergeUpdatedEventSetting(updatedEventSetting) {
      if (!updatedEventSetting?.eventKey) {
        return;
      }

      this.eventSettings = (this.eventSettings || []).map((eventSetting) =>
        eventSetting.eventKey === updatedEventSetting.eventKey
          ? updatedEventSetting
          : eventSetting
      );
    },
    scheduleEventAssignmentSave(eventKey) {
      if (this.saveTimersByEvent[eventKey]) {
        clearTimeout(this.saveTimersByEvent[eventKey]);
      }

      this.saveTimersByEvent[eventKey] = setTimeout(() => {
        this.persistEventAssignment(eventKey);
      }, 500);
    },
    async persistEventAssignment(eventKey) {
      if (
        !Object.prototype.hasOwnProperty.call(
          this.pendingEventAssignments,
          eventKey
        )
      ) {
        return;
      }

      const { templateId, enabled } = this.pendingEventAssignments[eventKey];
      delete this.pendingEventAssignments[eventKey];

      this.savingEventKey = eventKey;
      try {
        const updatedEventSetting = await this.$store.dispatch(
          UPDATE_NOTIFICATION_EVENT_SETTING,
          {
            eventKey,
            templateId,
            enabled,
          }
        );

        this.mergeUpdatedEventSetting(updatedEventSetting);
      } catch (error) {
        console.error('Error saving notification event setting:', error);
        this.$buefy.snackbar.open({
          message: this.$t('error'),
          type: 'is-danger',
        });
        await this.getNotificationEventSettings();
      } finally {
        this.savingEventKey = '';
      }
    },
    openCreateTemplate() {
      this.selectedTemplate = null;
      this.isEditorOpen = true;
    },
    openEditTemplate(template) {
      this.selectedTemplate = template;
      this.isEditorOpen = true;
    },
    closeEditor() {
      this.isEditorOpen = false;
      this.selectedTemplate = null;
    },
    async saveTemplate(payload) {
      this.isSaving = true;
      try {
        if (payload._id) {
          await this.$store.dispatch(UPDATE_NOTIFICATION_TEMPLATE, payload);
        } else {
          await this.$store.dispatch(CREATE_NOTIFICATION_TEMPLATE, payload);
        }

        await Promise.all([
          this.getNotificationTemplates({ search: this.search }),
          this.getNotificationEventSettings(),
        ]);
        this.closeEditor();
        this.$buefy.snackbar.open({
          message: this.$t('admin_page.template_saved_successfully'),
          type: 'is-success',
        });
      } catch (error) {
        console.error('Error saving notification template:', error);
        this.$buefy.snackbar.open({
          message: this.$t('error'),
          type: 'is-danger',
        });
      } finally {
        this.isSaving = false;
      }
    },
    async archiveTemplate(template) {
      this.$buefy.dialog.confirm({
        message: this.$t('admin_page.archive_notification_template_confirm'),
        type: 'is-warning',
        onConfirm: async () => {
          try {
            await this.$store.dispatch(ARCHIVE_NOTIFICATION_TEMPLATE, {
              id: template._id,
            });
            await Promise.all([
              this.getNotificationTemplates({ search: this.search }),
              this.getNotificationEventSettings(),
            ]);
            this.$buefy.snackbar.open({
              message: this.$t('admin_page.template_archived_successfully'),
              type: 'is-success',
            });
          } catch (error) {
            console.error('Error archiving notification template:', error);
            this.$buefy.snackbar.open({
              message: this.$t('error'),
              type: 'is-danger',
            });
          }
        },
      });
    },
    async saveEventAssignment({ eventKey, templateId, enabled }) {
      this.pendingEventAssignments[eventKey] = {
        templateId,
        enabled,
      };
      this.applyLocalEventAssignment(eventKey, templateId, enabled);
      this.scheduleEventAssignmentSave(eventKey);
    },
  },
  beforeDestroy() {
    Object.values(this.saveTimersByEvent).forEach((timerId) => {
      clearTimeout(timerId);
    });
  },
};
</script>

<style scoped>
.notification-templates-tab {
  padding: 16px;
}

.nt-header {
  margin-bottom: 12px;
}

.nt-header h3 {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 6px;
}

.nt-header p {
  margin: 0;
  color: #666;
}
</style>
